/**
 * BOGO voucher (type "4") item-picker helpers.
 *
 * When a BOGO voucher is applied but the cart cannot satisfy the deal
 * (empty cart / no matching items), the website opens a Buy/Get picker —
 * a separate flow modeled on the type-3 voucher picker, but driven by
 * `buyConfig` / `getConfig` instead of `selectedCategories`.
 *
 * This module converts each config into a "section" whose lists follow the
 * exact conventions the existing (type-3 generic) voucher filter plumbing
 * expects (`voucherFilterView` redux payload + `engineShowSpecialItem`):
 * arrays of `{ _id, name, isActive }` entries where only `isActive: true`
 * entries count.
 */

import { getItemPrice } from '@next-order/next-order-engine';

const getActive = (list) =>
  (list || []).filter((entry) => entry && entry.isActive === true);

// All selectable "sizes" of a menu item: plain sizes plus the submodifiers
// of complex (itemType 1) items, tagged with their parent modifier id.
const getItemSizes = (menuItem) => {
  const plainSizes = menuItem?.selectedSizes || [];
  const modifierSizes = (menuItem?.selectedModifiers || []).flatMap(
    (modifier) =>
      (modifier?.subModifiers || []).map((subModifier) => ({
        ...subModifier,
        modifierId: modifier._id,
      }))
  );
  return [...plainSizes, ...modifierSizes];
};

const buildSection = (config, isGetConfig, allMenuItems, label, key) => {
  const activeCategories = getActive(config?.categories);
  const activeItems = getActive(config?.menuItems);
  const activeSizes = getActive(config?.menuSizes);
  const activeSubModifiers = getActive(config?.subModifiers);
  // Spend trigger (buy side only): the Buy slot count is not fixed — the
  // picker adds one slot at a time until minSpend is reached, and anyItem
  // means "spend on anything".
  const isSpend = !isGetConfig && config?.triggerType === 'spend';
  const minSpend = isSpend ? Number(config?.minSpend) || 0 : 0;
  const anyItem = (isGetConfig || isSpend) && config?.anyItem === true;
  const capRaw = config?.maxItemPrice;
  const maxItemPrice =
    isGetConfig &&
    capRaw !== null &&
    capRaw !== undefined &&
    capRaw !== '' &&
    Number(capRaw) > 0
      ? Number(capRaw)
      : null;

  // Resolve the full menu items this section allows (same precedence as the
  // BOGO discount engine: anyItem > explicit menuItems > categories > all).
  let allowedItems;
  if (anyItem) {
    allowedItems = [...(allMenuItems || [])];
  } else if (activeItems.length > 0) {
    const allowedIds = new Set(activeItems.map((item) => item._id));
    allowedItems = (allMenuItems || []).filter((item) =>
      allowedIds.has(item._id)
    );
  } else if (activeCategories.length > 0) {
    const allowedCategoryIds = new Set(
      activeCategories.map((category) => category._id)
    );
    allowedItems = (allMenuItems || []).filter((item) =>
      allowedCategoryIds.has(item.categoryId)
    );
  } else {
    allowedItems = [...(allMenuItems || [])];
  }

  const hasSizeFilter = activeSizes.length > 0 || activeSubModifiers.length > 0;
  const allowedSizeIds = hasSizeFilter
    ? new Set(
        [...activeSizes, ...activeSubModifiers].map((size) => size._id)
      )
    : null;

  // maxItemPrice is a qualification cap (same rule as the discount engine):
  // a size above the cap simply does not qualify.
  const sizeQualifies = (size) =>
    (!allowedSizeIds || allowedSizeIds.has(size?._id)) &&
    (maxItemPrice === null || Number(size?.price) <= maxItemPrice);

  // Keep only items with at least one qualifying size.
  allowedItems = allowedItems.filter((menuItem) =>
    getItemSizes(menuItem).some(sizeQualifies)
  );

  // Size filter payload. When the config carries an explicit size filter we
  // pass it through unchanged. Otherwise ("any size") we synthesize the
  // lists from the qualifying sizes, because the downstream filter/popup
  // plumbing needs explicit size entries to work with.
  let menuSizes = activeSizes;
  let subModifiers = activeSubModifiers;
  if (!hasSizeFilter) {
    const sizesById = {};
    const subModifiersById = {};
    allowedItems.forEach((menuItem) => {
      (menuItem?.selectedSizes || []).forEach((size) => {
        if (sizeQualifies(size)) {
          sizesById[size._id] = {
            _id: size._id,
            name: size.name,
            isActive: true,
          };
        }
      });
      (menuItem?.selectedModifiers || []).forEach((modifier) => {
        (modifier?.subModifiers || []).forEach((subModifier) => {
          if (sizeQualifies(subModifier)) {
            subModifiersById[subModifier._id] = {
              _id: subModifier._id,
              name: subModifier.name,
              isActive: true,
              modifierId: modifier._id,
            };
          }
        });
      });
    });
    menuSizes = Object.values(sizesById);
    subModifiers = Object.values(subModifiersById);
  }

  // Categories payload: explicit config categories when set, otherwise the
  // categories the allowed items belong to (needed because the menu filter
  // only renders categories present in this list).
  const categories =
    activeCategories.length > 0 && !anyItem
      ? activeCategories.map((category) => ({
          _id: category._id,
          name: category.name,
          isActive: true,
        }))
      : Object.values(
          allowedItems.reduce((acc, item) => {
            if (item.categoryId && !acc[item.categoryId]) {
              acc[item.categoryId] = {
                _id: item.categoryId,
                name: item.categoryName || '',
                isActive: true,
              };
            }
            return acc;
          }, {})
        );

  // Short human description for an empty slot box.
  let description = '';
  if (anyItem) {
    description = 'Any item';
  } else {
    const sizeNames = [...activeSizes, ...activeSubModifiers]
      .map((size) => size.name)
      .filter(Boolean);
    const categoryNames = activeCategories
      .map((category) => category.name)
      .filter(Boolean);
    description = [sizeNames.join('/'), categoryNames.join(' or ')]
      .filter(Boolean)
      .join(' ');
  }

  const quantity = isSpend
    ? 0
    : Number(config?.quantity) > 0
      ? Math.floor(Number(config.quantity))
      : 1;

  return {
    key,
    label,
    quantity,
    isSpend,
    minSpend,
    anyItem,
    maxItemPrice,
    categories,
    menuItems: allowedItems.map((item) => ({
      _id: item._id,
      name: item.name,
      isActive: true,
    })),
    menuSizes,
    subModifiers,
    description,
  };
};

// Returns [buySection, getSection] for the picker. Slot indexes are global:
// buy slots occupy 0..buyQty-1, get slots buyQty..buyQty+getQty-1. Spend
// deals have a growing Buy section, so their get slots take 0..getQty-1 and
// buy slots start at getQty.
export const buildBogoVoucherSections = (voucher, allMenuItems) => [
  buildSection(voucher?.buyConfig, false, allMenuItems, 'Buy', 'buy'),
  buildSection(voucher?.getConfig, true, allMenuItems, 'Get', 'get'),
];

// Estimate rows for the picker's live discount preview: one row per filled
// slot, in the same shape the BOGO discount engine consumes. `keyOffset`
// keeps primaryKeys unique when the rows are combined with cart rows.
export const buildBogoEstimateRows = (selectedItems, keyOffset = 0) =>
  (selectedItems || []).map((item, index) => {
    let sizeKey = '';
    let price = 0;
    const chosenSize =
      (item.selectedSizes || []).find((size) => Number(size.quantity) > 0) ||
      (item.selectedSizes || [])[0];
    if (chosenSize) {
      sizeKey = chosenSize._id;
      price = Number(chosenSize.price) || 0;
    } else {
      // complex item — chosen size lives in the base modifier's submodifiers
      for (const modifier of item.selectedModifiers || []) {
        if (modifier._id === item.baseModifierId) {
          const chosenSubModifier = (modifier.subModifiers || []).find(
            (subModifier) => Number(subModifier.quantity) > 0
          );
          if (chosenSubModifier) {
            sizeKey = chosenSubModifier._id;
            price = Number(chosenSubModifier.price) || 0;
          }
        }
      }
    }
    return {
      primaryKey: keyOffset + index + 1,
      _id: item._id,
      categoryId: item.categoryId,
      sizeKey,
      price,
      selectedExtraIngredients: item.selectedExtraIngredients,
      isHalf: false,
      isSecondHalf: false,
    };
  });

/* --------------------- editing an applied BOGO deal ---------------------- */

export const getLineQuantity = (item) =>
  Number(item?.quantity) || Number(item?.selectedSizes?.[0]?.quantity) || 1;

const scaleSelected = (entry, quantity) => {
  if (Number(entry?.quantity) > 0) entry.quantity = quantity;
};

// Sets a cart line's quantity, only touching options already chosen on the
// line (extras stay per-unit). Unlike engineSetItemQuantity this works on the
// given line, so duplicate lines of the same item are not mixed up.
export const setLineQuantity = (item, quantity, halfHalfSetup) => {
  const line = JSON.parse(JSON.stringify(item));
  line.quantity = quantity;
  (line.selectedSizes || []).forEach((size) => {
    if (!(Number(size.quantity) > 0)) return;
    size.quantity = quantity;
    (size.variants || []).forEach((variant) => scaleSelected(variant, quantity));
  });
  if (line.itemType === 1) {
    (line.selectedModifiers || []).forEach((modifier) => {
      (modifier.subModifiers || []).forEach((subModifier) => {
        if (modifier._id === line.baseModifierId) {
          if (!(Number(subModifier.quantity) > 0)) return;
          subModifier.quantity = quantity;
          (subModifier.variants || []).forEach((variant) =>
            scaleSelected(variant, quantity)
          );
          return;
        }
        (subModifier.prices || []).forEach((price) => {
          if (!(Number(price.quantity) > 0)) return;
          price.quantity = quantity;
          (price.variants || []).forEach((variant) =>
            scaleSelected(variant, quantity)
          );
        });
      });
    });
  }
  const itemPrice = Number(getItemPrice(line, halfHalfSetup));
  line.itemPrice = itemPrice;
  line.payablePrice = itemPrice;
  return line;
};

const getConfigQuantity = (config) =>
  Number(config?.quantity) > 0 ? Math.floor(Number(config.quantity)) : 1;

/**
 * Turns the cart units an applied BOGO deal consumed back into picker slot
 * selections, using the same boxIndex layout as the picker (quantity deals:
 * buy 0..buyQty-1 then get; spend deals: get 0..getQty-1 then buy).
 * Only one application fits the picker; units beyond the slots stay in the
 * cart untouched.
 *
 * Returns the selections, `editUnits` ({ [orderIndex]: units moved into the
 * picker }) and the first boxIndex of each cart-card row (`buy-<orderIndex>`
 * / `get-<orderIndex>`).
 */
export const buildBogoEditState = (
  voucher,
  voucherItems,
  menuItems,
  halfHalfSetup
) => {
  const isSpend = voucher?.buyConfig?.triggerType === 'spend';
  const getQuantity = getConfigQuantity(voucher?.getConfig);
  const buyQuantity = isSpend ? Infinity : getConfigQuantity(voucher?.buyConfig);
  const next = { buy: isSpend ? getQuantity : 0, get: isSpend ? 0 : buyQuantity };
  const end = {
    buy: isSpend ? Infinity : buyQuantity,
    get: next.get + getQuantity,
  };

  const selectedSpecials = [];
  const editUnits = {};
  const boxIndexByRow = {};

  (voucherItems || []).forEach((voucherItem) => {
    const line = (menuItems || []).find(
      (item) =>
        item.orderIndex === voucherItem.orderIndex &&
        item._id === voucherItem._id
    );
    if (!line) return;
    const getUnits = Number(voucherItem.voucherGetQuantity) || 0;
    const units = {
      buy: Math.max((Number(voucherItem.voucherQuantity) || 0) - getUnits, 0),
      get: getUnits,
    };
    const {
      orderIndex,
      voucherId,
      isVoucherItem,
      isSpecialItem,
      boxIndex,
      bogoPin,
      ...lineData
    } = line;
    const unit = setLineQuantity(lineData, 1, halfHalfSetup);

    ['buy', 'get'].forEach((role) => {
      for (let i = 0; i < units[role] && next[role] < end[role]; i++) {
        const slotIndex = next[role]++;
        const rowKey = `${role}-${orderIndex}`;
        if (boxIndexByRow[rowKey] === undefined) {
          boxIndexByRow[rowKey] = slotIndex;
        }
        editUnits[orderIndex] = (editUnits[orderIndex] || 0) + 1;
        selectedSpecials.push({
          ...JSON.parse(JSON.stringify(unit)),
          voucherId: voucher._id,
          boxIndex: slotIndex,
          isHalf: false,
        });
      }
    });
  });

  return { selectedSpecials, editUnits, boxIndexByRow };
};

// Role of a picker slot, from the same boxIndex layout as buildBogoEditState.
export const getBogoSlotRole = (bogoSections, boxIndex) => {
  const buySection = (bogoSections || []).find((s) => s.key === 'buy');
  const getSection = (bogoSections || []).find((s) => s.key === 'get');
  if (buySection?.isSpend) {
    return boxIndex >= (getSection?.quantity || 0) ? 'buy' : 'get';
  }
  return boxIndex < (buySection?.quantity || 0) ? 'buy' : 'get';
};

export const stripBogoPins = (menuItems) =>
  (menuItems || []).map(({ bogoPin, ...item }) => item);

// Cart lines with the units being edited taken out.
export const removeBogoEditUnits = (menuItems, editUnits, halfHalfSetup) =>
  (menuItems || []).reduce((acc, item) => {
    const units = Number(editUnits?.[item.orderIndex]) || 0;
    if (!units || item.isHalf) {
      acc.push(item);
      return acc;
    }
    const remaining = getLineQuantity(item) - units;
    if (remaining > 0) acc.push(setLineQuantity(item, remaining, halfHalfSetup));
    return acc;
  }, []);

// Cart rows (one per unit) without the units currently loaded in the picker,
// so spend progress and the discount preview don't count them twice.
export const excludeBogoEditRows = (rows, editUnits) => {
  if (!editUnits) return rows;
  const remaining = { ...editUnits };
  return rows.filter((row) => {
    if (remaining[row.orderIndex] > 0) {
      remaining[row.orderIndex] -= 1;
      return false;
    }
    return true;
  });
};
